/**
 * Launchpad hooks — W3-FE5
 *
 * Hooks for the ROROS onboarding launchpad and tenant settings.
 * Previously inlined in hooks/roros-queries.ts; moved here for consistent
 * hook layout with the rest of the roros hook modules.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosLaunchpadService } from '@/services/RorosService';
import { rorosKeys } from './keys';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

export function useRorosLaunchpadStatus() {
  return useQuery({
    queryKey: rorosKeys.launchpad.status,
    queryFn: () => RorosLaunchpadService.getStatus(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosLaunchpadSubmitStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ step, data }: { step: string; data: Record<string, unknown> }) =>
      RorosLaunchpadService.submitStep(step, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.launchpad.status });
    },
  });
}

export function useRorosLaunchpadComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => RorosLaunchpadService.complete(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.launchpad.status });
    },
  });
}

export function useRorosTenantSettings() {
  return useQuery({
    queryKey: ['roros', 'tenant-settings'] as const,
    queryFn: () => RorosLaunchpadService.getTenantSettings(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosTenantSettingsUpdate() {
  const qc = useQueryClient();
  return useMutation({
    // M18.2 (#21): payload uses planningCycle (camelCase) — matches backend schema.
    // J6: payload also accepts operatingMode + decisionNoteId (GOV-03 server gate).
    mutationFn: (payload: {
      planningCycle?: 'quarterly' | 'trimester';
      operatingMode?: 'founder' | 'growth' | 'scaling';
      decisionNoteId?: string;
    }) => RorosLaunchpadService.updateTenantSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roros', 'tenant-settings'] });
      // Planning cycle switching flips cadence activation server-side (next
      // PUT via CadencePanel), so also invalidate the schedules list.
      qc.invalidateQueries({ queryKey: ['roros', 'cadence', 'schedules'] });
      // Mode promotion creates a Monthly Mode Check cadence row server-side.
      qc.invalidateQueries({ queryKey: ['roros', 'cadence', 'schedules'] });
    },
  });
}
